/**
 * The pending line (part 10, N1, N6, N8; part 10a, table W) and the blank line holding the cursor (part 10a, table C):
 * each drawn as the line typing there makes.
 */
import { reparseChapter } from '@cockatrice/core';
import { ChangeSet, EditorSelection, StateEffect, StateField } from '@codemirror/state';
import type { EditorState, Extension, Range } from '@codemirror/state';
import { Decoration, ViewPlugin, WidgetType } from '@codemirror/view';
import type { DecorationSet, EditorView, PluginValue } from '@codemirror/view';
import { chapterField } from './chapter-state.ts';
import { LIVE_CLASS, liveDecorations } from './live.ts';
import { isSeparator, LOOK_CLASS, lookLines, paragraphLook } from './look.ts';
import type { LookLine } from './look.ts';
import { plainPieces } from './paste.ts';
import { rewrite } from './rewrite.ts';
import type { Edit } from './rewrite.ts';
import { TextWidget } from './view.ts';
import { pendingEdit } from './structure.ts';
import type { Pending } from './structure.ts';

/**
 * Part 10a, table W: the line the next typed character goes on, as it will look: its line classes (table D's and the
 * look's: a quote's rule, the gap above it, its indent) and the container marks before the text; for a block pending
 * above, the look's classes of the block after it.
 */
export interface PendingShape {
  readonly classes: readonly string[];
  readonly prefix: string;
  readonly after: readonly string[];
}

/** The classes of line decorations asked for one line, one by one and each once (as the line holds them), in their order. */
function lineClasses(lines: readonly LookLine[]): string[] {
  return [...new Set(lines.flatMap((line) => line.className.split(' ')))];
}

/**
 * Principle 3: the line a typed letter goes on when `edit` puts it in (the letter its last character), as it will look:
 * its line classes (table D's and the look's) and the text before the letter. Pure; one rewrite and one reparse of what
 * it changes.
 */
function typedLine(state: EditorState, edit: Edit): { readonly classes: string[]; readonly prefix: string; readonly after: (n: number) => string[] } {
  const parsed = state.field(chapterField);
  const look = state.facet(paragraphLook);
  const typed = rewrite(parsed, [edit]);
  const doc = ChangeSet.of(typed.changes, state.doc.length).apply(state.doc);
  const { tree } = reparseChapter(parsed, typed.changes);
  // The cursor will be after the letter.
  const at = typed.points.reduce((_, p) => p.end, edit.from) - 1;
  const line = doc.lineAt(at);
  // Table D's line classes never read the selection; the editor's own is given.
  const live = liveDecorations(tree, doc, { from: line.from, to: line.to }, state.selection.ranges).decorations.flatMap((d) => (d.kind === 'line' ? [d] : []));
  return {
    classes: lineClasses([...live, ...lookLines(tree, doc, line.from, line.to, look)]),
    prefix: doc.sliceString(line.from, at),
    // The look's classes of the line `n` lines after the letter's.
    after: (n) => {
      const next = doc.line(line.number + n);
      return lineClasses(lookLines(tree, doc, next.from, next.from, look));
    },
  };
}

/** Table W (principle 3): the pending line's shape, read from the edit a typed letter makes there, in the look shown. */
export function pendingShape(state: EditorState, pending: Pending): PendingShape {
  const typed = typedLine(state, pendingEdit(pending, plainPieces('a')));
  // W9: the block follows two lines on, after the blank line its pending markup puts between them (N1).
  return { classes: typed.classes, prefix: typed.prefix, after: pending.kind === 'above' ? typed.after(2) : [] };
}

/** C9 (*deep-fix r9*): a line a press kept, pressed in its turn: pending again. */
export const revivePending = StateEffect.define<Pending>();

/** A pending block or break: a line of its own with a caret, where the next text typed goes (N1, N6), as table W shapes it. */
export class PendingWidget extends WidgetType {
  readonly kind: Pending['kind'];
  readonly shape: PendingShape;
  /** What is pending (or was, when only a press keeps the line). */
  readonly pending: Pending;
  /** Where the cursor stays while it is pending. */
  readonly at: number;
  /** Still pending, with its caret; or only kept in place by a press (C9), with none. */
  readonly live: boolean;

  constructor(pending: Pending, shape: PendingShape, live: boolean) {
    super();
    this.pending = pending;
    this.kind = pending.kind;
    this.shape = shape;
    this.at = pending.at;
    this.live = live;
  }

  override eq(other: PendingWidget): boolean {
    const [a, b] = [this.shape, other.shape];
    return other.kind === this.kind && other.at === this.at && other.live === this.live && a.prefix === b.prefix && a.classes.join(' ') === b.classes.join(' ') && a.after.join(' ') === b.after.join(' ');
  }

  /**
   * A line with the typed line's classes, its marks and the caret. A press on it puts the cursor back where the line is
   * pending, which keeps it (N8, *deep-fix r23*).
   */
  toDOM(view: EditorView): HTMLElement {
    const line = document.createElement('div');
    line.className = ['cm-line', 'ct-pending-line', `ct-pending-${this.kind}`, ...this.shape.classes].join(' ');
    const marks = this.shape.prefix === '' ? [] : [Object.assign(document.createElement('span'), { className: LIVE_CLASS.markup, textContent: this.shape.prefix })];
    // A line kept by a press keeps its caret's room, unseen, so its height doesn't change at the press (C9).
    line.append(...marks, Object.assign(document.createElement('span'), { className: this.live ? 'ct-pending-caret' : 'ct-pending-caret ct-pending-held' }));
    line.addEventListener('mousedown', (event) => {
      event.preventDefault();
      view.focus();
      // A line only kept by a press (C9) is made pending again (*deep-fix r9*: the text typed next joined the block before).
      view.dispatch({ selection: EditorSelection.cursor(this.at), effects: this.live ? [] : revivePending.of(this.pending), userEvent: 'select.pointer' });
    });
    return line;
  }
}

/**
 * Table W: the pending line, as decorations: a line of its own before the block's first line (above) or after the line
 * the pending point ends; for a block above, the look of the block after it on that block's line (W9).
 */
export function pendingDecorations(state: EditorState, pending: Pending | null, live = true): DecorationSet {
  if (pending === null) return Decoration.none;
  const shape = pendingShape(state, pending);
  const line = state.doc.lineAt(pending.from);
  const widget = Decoration.widget({ widget: new PendingWidget(pending, shape, live), block: true, side: pending.kind === 'above' ? -1 : 1 });
  if (pending.kind !== 'above') return Decoration.set([widget.range(line.to)]);
  // In order: a block widget before a line sorts ahead of that line's classes.
  return Decoration.set([widget.range(line.from), ...shape.after.map((className) => Decoration.line({ class: className }).range(line.from))]);
}

/**
 * C9 (*deep-fix r6, r7*): what a press of the mouse keeps in place: the blank line shown and the line pending when it
 * began. They stay until a key is pressed or the text changes, never at a mouse event: a line collapsing at a press, a
 * release or between the presses of a double-click moves every line below, and CodeMirror reads the next press or move
 * against the new layout (r6: a click with a pixel of drift selected into the next paragraph; r7: a double-click's second
 * press took the word a line lower). A key or a change moves the layout only when no press is in flight.
 */
export interface Held {
  /** The starts of the blank lines kept, in the order the presses kept them (*deep-fix r10*: one line was kept, and a
   * second press let the first go). */
  readonly lines: readonly number[];
  readonly pending: Pending | null;
}

/** Holds (a press began) or lets go (a key, a change). */
const hold = StateEffect.define<Held | null>();

/** C9: the lines a press keeps in place; a change of the text lets them go. */
export const heldField: StateField<Held | null> = StateField.define<Held | null>({
  create: () => null,
  update: (value, transaction) => {
    for (const effect of transaction.effects) if (effect.is(hold)) return effect.value;
    return transaction.docChanged ? null : value;
  },
});

/** The start of the blank line the cursor stands on, or null (a range, a shown line, no chapter). */
function caretBlank(state: EditorState): number | null {
  const parsed = state.field(chapterField, false);
  const { main } = state.selection;
  const line = state.doc.lineAt(main.head);
  return parsed === undefined || !main.empty || !isSeparator(parsed.tree, line) ? null : line.from;
}

/** The blank line starting at `from`, shown as the line a letter typed at `at` on it makes (table C). */
function blankLine(state: EditorState, from: number, at: number): Range<Decoration>[] {
  const line = state.doc.lineAt(from);
  // A letter placed on the blank line as P4 places it (typing and pasting a letter differ only in values and marks).
  const typed = typedLine(state, { from: at, to: at, insert: plainPieces('a'), place: true });
  // The typed line's marks begin with what the blank line holds (its quote marks, its blanks; P4 puts the letter after
  // them wherever on the line the cursor is): the rest is drawn at the line's end.
  const rest = typed.prefix.slice(line.length);
  const shown = Decoration.line({ class: [LOOK_CLASS.caret, ...typed.classes].join(' ') }).range(line.from);
  if (rest === '') return [shown];
  // A line's classes sort ahead of a widget at its start.
  return [shown, Decoration.widget({ widget: new TextWidget(rest, LIVE_CLASS.markup), side: -1 }).range(line.to)];
}

/**
 * Table C (the user's choice after deep-fix r4): the blank line the cursor stands on, which the look hides otherwise,
 * drawn as the line typing there makes (part 10, P4): shown, with that line's classes (its gap, indent, a quote's rule),
 * and the container marks it will have that the blank line doesn't hold yet, at the line's end; and one a press keeps in
 * place (C9) while it is still blank. A state with the input rules but no chapter (the app reads the editor's keys from
 * one) shows nothing.
 */
export function caretLineDecorations(state: EditorState): DecorationSet {
  const here = caretBlank(state);
  const ranges = here === null ? [] : blankLine(state, here, state.selection.main.head);
  // A kept line is still blank: only a change of the text makes it otherwise, and that lets it go.
  for (const held of state.field(heldField)?.lines ?? []) if (held !== here) ranges.push(...blankLine(state, held, state.doc.lineAt(held).to));
  return Decoration.set(ranges, true);
}

/** Keys that alone change nothing (a Shift+click begins with one): they never let go (H9b, *deep-fix r10*). */
const MODIFIERS: ReadonlySet<string> = new Set(['Shift', 'Control', 'Alt', 'Meta', 'AltGraph', 'CapsLock', 'Fn', 'OS', 'Hyper', 'Super']);

class PointerHold implements PluginValue {
  private readonly view: EditorView;
  private readonly pendingOf: (state: EditorState) => Pending | null;
  /** Clears `down` at the release, wherever it is (H9c); null with no button down. */
  private release: (() => void) | null = null;

  constructor(view: EditorView, pendingOf: (state: EditorState) => Pending | null) {
    this.view = view;
    this.pendingOf = pendingOf;
  }

  /** C9, H10: a press keeps the shown blank line and the pending line, joining what earlier presses kept. */
  press(event: MouseEvent): void {
    if (event.button !== 0) return;
    this.watchRelease();
    const { state } = this.view;
    const kept = state.field(heldField);
    const here = caretBlank(state);
    const lines = [...(kept?.lines ?? []), ...(here === null || kept?.lines.includes(here) === true ? [] : [here])];
    const pending = this.pendingOf(state) ?? kept?.pending ?? null;
    if (lines.length === 0 && pending === null) return;
    this.view.dispatch({ effects: hold.of({ lines, pending }) });
  }

  /** H9: a key lets go of what presses kept, but a modifier alone (H9b) or any key while a button is down (H9c). */
  key(event: KeyboardEvent): void {
    if (MODIFIERS.has(event.key) || this.release !== null || this.view.state.field(heldField) === null) return;
    this.view.dispatch({ effects: hold.of(null) });
  }

  destroy(): void {
    this.release?.();
  }

  /** Notes the button down until it is released, in the editor or anywhere in the page. */
  private watchRelease(): void {
    this.release?.();
    const document = this.view.dom.ownerDocument;
    const up = (): void => {
      document.removeEventListener('mouseup', up);
      this.release = null;
    };
    document.addEventListener('mouseup', up);
    this.release = up;
  }
}

/** C9: the press that keeps lines in place, `pendingOf` reading the line pending (part 10's field). */
export function pointerHold(pendingOf: (state: EditorState) => Pending | null): Extension {
  return [
    heldField,
    ViewPlugin.define((view) => new PointerHold(view, pendingOf), {
      eventObservers: {
        mousedown(event) {
          this.press(event);
        },
        keydown(event) {
          this.key(event);
        },
      },
    }),
  ];
}
